import { memo } from "react"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import TagBadgeIcon from "@/shared/icons/totodoicons/fill/tag-badge.svg?react"

interface TaskTagsProps {
  task: DbObject
  className?: string
  /** Force icon+count mode regardless of breakpoint */
  iconOnly?: boolean
}

/**
 * Compact tag indicator for task rows.
 *
 * Desktop: shows first 2 tag chips + "+N" overflow.
 * Mobile: shows tag icon with count overlay.
 * iconOnly: always shows tag icon with count (for compact/banner use).
 *
 * Matches v1 TaskListTags pattern.
 */
export const TaskTags = memo(function TaskTags({
  task,
  className,
  iconOnly,
}: TaskTagsProps) {
  const tags = task.tags
  if (!tags || tags.length === 0) return null

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {/* Desktop: show first 2 chips (hidden when iconOnly) */}
      {!iconOnly && (
        <div className="hidden items-center gap-0.5 sm:flex">
          {tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="inline-flex max-w-20 items-center truncate rounded-full bg-secondary/50 px-1.5 py-px text-[10px] font-medium text-secondary-foreground/70"
            >
              {tag}
            </span>
          ))}
          {tags.length > 2 && (
            <span className="text-[10px] text-muted-foreground/60">
              +{tags.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Mobile / iconOnly: tag icon with count overlay (v1 style) */}
      <div className={cn("relative inline-flex items-center justify-center", iconOnly ? "" : "sm:hidden")}>
        <TagBadgeIcon className="size-3.5 text-primary/25" />
        {tags.length > 1 && (
          <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold leading-none text-primary -rotate-45">
            {tags.length}
          </span>
        )}
      </div>
    </div>
  )
})
